import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import BackButton from '../components/BackButton';
import { useDemo } from '../context/DemoContext';
import {
  Skull, Send, Clock, AlertCircle, CheckCircle, XCircle, Loader2,
  Users, Hash, Trophy, Lock, Shield, Zap, Star, TrendingUp, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FEES_PER_FAILED_TX = 50000;
const SP_PER_TX = 25;
const DAILY_LIMIT = 10;

// ═══ LOCKED GRAVEYARD ═══
const LockedGraveyard = () => {
  const LockedPage = require('../components/LockedPage').default;
  return (
    <LockedPage
      title="TX GRAVEYARD"
      subtitle="Bury failed transactions. Profit from their demise."
      lore="Every dead transaction on Solana is worth something down here. Submit a failed TX hash, our gremlins verify it on-chain, and you earn 50K fees + 25 Season Points per kill. The graveyard opens at $GFG launch."
      statusLabel="Sealed"
      tickerText="Scanning for dead transactions..."
      theme={{ primary: '#8b5cf6', glow: '#7c3aed', bg: '#08020e' }}
      particles="wisps"
      features={[
        { icon: Send, title: 'Submit Failed TX', desc: 'Paste any failed Solana transaction hash' },
        { icon: Hash, title: 'On-Chain Verification', desc: 'Gremlins verify failure via Solana RPC' },
        { icon: Skull, title: '50K Fees + 25 SP', desc: 'Per verified dead transaction buried' },
        { icon: Trophy, title: 'Grave Digger Board', desc: 'Top grave diggers ranked by total kills' },
      ]}
      bottomCTA={
        <div className="flex items-center justify-between gap-4 px-5 py-3"
          style={{ background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.2)' }}>
          <div className="flex items-center gap-2">
            <Skull className="w-4 h-4" style={{ color: '#8b5cf6' }} />
            <span className="text-[10px] tracking-wider uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(167,139,250,0.7)' }}>Per Verified TX</span>
            <span className="text-sm font-bold" style={{ fontFamily: "'Impact', sans-serif", color: '#a78bfa' }}>+50K fees +25 SP</span>
          </div>
          <button onClick={() => window.open('https://discord.gg/3wH7NaVXxP', '_blank')} className="px-4 py-1.5 text-xs font-bold tracking-wider uppercase transition-all hover:scale-105"
            style={{ fontFamily: "'Cinzel', serif", background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.3)', color: '#8b5cf6' }}>
            Discord
          </button>
        </div>
      }
    />
  );
};

// ═══ PANEL ═══
const Panel = ({ children, className = '', glow = false, style = {}, ...props }) => (
  <div className={`relative overflow-hidden ${className}`} {...props}
    style={{
      background: glow ? 'rgba(12,4,20,0.85)' : 'rgba(8,3,16,0.8)',
      border: `1px solid rgba(139,92,246,${glow ? '0.35' : '0.18'})`,
      backdropFilter: 'blur(10px)',
      boxShadow: glow
        ? '0 0 25px rgba(139,92,246,0.1), inset 0 0 20px rgba(139,92,246,0.04)'
        : '0 0 10px rgba(139,92,246,0.03), inset 0 0 10px rgba(139,92,246,0.02)',
      ...style,
    }}>
    <div className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l z-10" style={{ borderColor: 'rgba(167,139,250,0.4)' }} />
    <div className="absolute top-0 right-0 w-2.5 h-2.5 border-t border-r z-10" style={{ borderColor: 'rgba(167,139,250,0.4)' }} />
    <div className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l z-10" style={{ borderColor: 'rgba(167,139,250,0.4)' }} />
    <div className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r z-10" style={{ borderColor: 'rgba(167,139,250,0.4)' }} />
    <motion.div className="absolute inset-0 pointer-events-none"
      style={{ background: 'linear-gradient(135deg, transparent 40%, rgba(139,92,246,0.04) 50%, transparent 60%)', backgroundSize: '300% 300%' }}
      animate={{ backgroundPosition: ['0% 0%', '100% 100%'] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'linear' }} />
    {children}
  </div>
);

// ═══ TOMBSTONE CARD ═══
const Tombstone = ({ tx, index }) => {
  const shortTx = `${tx.tx_hash.slice(0, 8)}...${tx.tx_hash.slice(-6)}`;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.06 }}
      className="relative overflow-hidden" data-testid={`tombstone-${index}`}
      style={{ background: 'rgba(8,3,16,0.7)', border: '1px solid rgba(139,92,246,0.12)' }}>
      <div className="p-3.5 relative z-10">
        <div className="flex items-center justify-between mb-1.5">
          <code className="text-xs font-bold" style={{ fontFamily: "'Courier New', monospace", color: '#a78bfa' }}>{shortTx}</code>
          <span className="text-xs font-bold px-2 py-0.5" style={{
            fontFamily: "'Impact', sans-serif", color: '#a78bfa',
            background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)',
            transform: 'skewX(-8deg)',
          }}>+{(tx.fees_earned || FEES_PER_FAILED_TX).toLocaleString()}</span>
        </div>
        <p className="text-[10px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(167,139,250,0.35)' }}>
          {new Date(tx.submitted_at).toLocaleDateString()}
        </p>
      </div>
    </motion.div>
  );
};


export default function FailedTxGraveyard() {
  const { features, loading: flagsLoading } = useFeatureFlags();
  const { isDemo } = useDemo();
  if (flagsLoading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#08020e' }}>
      <motion.div animate={{ opacity: [0.3, 1, 0.3], scale: [0.9, 1.1, 0.9] }} transition={{ duration: 2, repeat: Infinity }}>
        <Skull className="w-10 h-10" style={{ color: '#8b5cf6', filter: 'drop-shadow(0 0 20px rgba(139,92,246,0.6))' }} />
      </motion.div>
    </div>
  );
  if (!features.graveyard && !isDemo) return <LockedGraveyard />;
  return <UnlockedGraveyard />;
}

function UnlockedGraveyard() {
  const navigate = useNavigate();
  const { publicKey, connected: walletConnected } = useWallet();
  const { isDemo, demoWallet } = useDemo();
  const connected = walletConnected || isDemo;
  const walletAddress = publicKey?.toBase58() || (isDemo ? demoWallet : undefined);

  const [txHash, setTxHash] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [graveyardStats, setGraveyardStats] = useState(null);
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/graveyard/stats`);
        const data = await res.json();
        setGraveyardStats(data);
        setRecentSubmissions(data.recent_submissions || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!walletAddress) return;
    const fetchUser = async () => {
      try { const res = await fetch(`${API_URL}/api/graveyard/user/${walletAddress}`); setUserStats(await res.json()); } catch {}
    };
    fetchUser();
  }, [walletAddress]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!txHash.trim() || !walletAddress || isSubmitting) return;
    setIsSubmitting(true); setSubmitResult(null);
    try {
      const res = await fetch(`${API_URL}/api/graveyard/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet_address: walletAddress, tx_hash: txHash.trim() }),
      });
      const data = await res.json();
      setSubmitResult(data);
      if (data.success) {
        setTxHash('');
        const [statsRes, userRes] = await Promise.all([
          fetch(`${API_URL}/api/graveyard/stats`),
          fetch(`${API_URL}/api/graveyard/user/${walletAddress}`),
        ]);
        setGraveyardStats(await statsRes.json());
        const statsData = await statsRes.json().catch(() => null);
        if (statsData) setRecentSubmissions(statsData.recent_submissions || []);
        setUserStats(await userRes.json());
      }
    } catch { setSubmitResult({ success: false, message: 'Failed to submit. Try again.' }); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="min-h-screen relative text-white" data-testid="graveyard-page"
      style={{ background: '#08020e' }}>

      <style>{`
        @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
      `}</style>

      {/* Background */}
      <div className="fixed inset-0 z-0" style={{ background: 'radial-gradient(ellipse at 50% 20%, #150828 0%, #0d0418 40%, #08020e 100%)' }} />
      {/* Midjourney background — The Digital Necropolis */}
      <div className="fixed inset-0 z-[1] pointer-events-none">
        <img src="/images/graveyard/necropolis_bg.png" alt=""
          className="w-full h-full object-cover"
          style={{ opacity: 0.15, mixBlendMode: 'screen' }} />
      </div>
      <div className="fixed inset-0 z-[1] pointer-events-none">
        <motion.div className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at 50% 25%, rgba(139,92,246,0.08) 0%, transparent 50%)' }}
          animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 4, repeat: Infinity }} />
      </div>

      {/* CRT */}
      <div className="fixed inset-0 z-[60] pointer-events-none mix-blend-overlay" style={{
        background: 'linear-gradient(rgba(18,16,16,0) 50%, rgba(0,0,0,0.18) 50%)', backgroundSize: '100% 4px', opacity: 0.1,
      }} />

      {/* Particles — purple wisps */}
      <div className="fixed inset-0 z-[2] pointer-events-none overflow-hidden">
        {[...Array(10)].map((_, i) => (
          <motion.div key={`w-${i}`} className="absolute rounded-full"
            style={{
              left: `${5 + i * 10}%`, bottom: '-15px',
              width: `${18 + Math.random() * 25}px`, height: `${18 + Math.random() * 25}px`,
              background: `radial-gradient(circle, rgba(139,92,246,0.2) 0%, transparent 70%)`, filter: 'blur(3px)',
            }}
            animate={{ y: [0, -650], opacity: [0, 0.5, 0.15, 0], scale: [0.5, 1.2, 0.6] }}
            transition={{ duration: 8 + Math.random() * 4, delay: i * 1.2, repeat: Infinity, ease: 'easeOut' }}
          />
        ))}
      </div>

      <BackButton to="/" />

      {/* ═══ CENTERED CONTENT ═══ */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 pt-20 pb-16">

        {/* ═══ HEADER ═══ */}
        <div className="mb-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div className="relative">
              <div className="absolute -inset-x-6 -inset-y-3 pointer-events-none" style={{
                background: 'radial-gradient(ellipse at 40% 60%, rgba(139,92,246,0.1) 0%, transparent 70%)', filter: 'blur(12px)',
              }} />
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] uppercase tracking-[0.5em] mb-1 relative"
                style={{ fontFamily: "'Cinzel', serif", color: 'rgba(167,139,250,0.65)' }}>
                Rest In Peace, Failed Transactions
              </motion.p>
              <motion.h1 initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, type: 'spring', stiffness: 100 }}
                className="relative text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-wider inline-block"
                style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif" }} data-testid="graveyard-title">
                <span className="absolute inset-0" style={{ color: 'transparent', WebkitTextStroke: '2px rgba(139,92,246,0.25)', filter: 'blur(6px)' }}>TX GRAVEYARD</span>
                <span className="relative" style={{
                  background: 'linear-gradient(180deg, #ede9fe 0%, #c4b5fd 15%, #a78bfa 30%, #8b5cf6 50%, #7c3aed 70%, #6d28d9 90%)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 0 25px rgba(139,92,246,0.5)) drop-shadow(0 0 50px rgba(139,92,246,0.2))',
                }}>TX GRAVEYARD</span>
                <motion.span className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(196,181,253,0.4) 50%, transparent 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundSize: '200% 100%' }}
                  animate={{ backgroundPosition: ['200% 0%', '-200% 0%'] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}>TX GRAVEYARD</motion.span>
              </motion.h1>
            </div>

            {/* Reward badge */}
            <div className="flex items-center gap-2 px-5 py-2.5"
              style={{ border: '1px solid rgba(139,92,246,0.35)', background: 'rgba(139,92,246,0.08)', boxShadow: '0 0 15px rgba(139,92,246,0.08)' }}>
              <Skull className="w-4 h-4" style={{ color: '#a78bfa', filter: 'drop-shadow(0 0 4px rgba(139,92,246,0.5))' }} />
              <span className="text-lg font-bold tracking-wider" style={{
                fontFamily: "'Impact', sans-serif",
                background: 'linear-gradient(180deg, #c4b5fd, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>+{FEES_PER_FAILED_TX.toLocaleString()} fees +{SP_PER_TX} SP</span>
              <span className="text-[10px] font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(167,139,250,0.45)' }}>/TX</span>
            </div>
          </div>

          <motion.div initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-3 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #8b5cf6, #c084fc, #8b5cf6, transparent)' }} />
        </div>

        {/* ═══ STATS ROW ═══ */}
        <div className="grid grid-cols-3 gap-2 md:gap-3 mb-4 md:mb-6" data-testid="graveyard-stats-grid">
          {[
            { label: 'Buried TXs', value: graveyardStats?.total_submissions || 0, icon: Hash, color: '#a78bfa' },
            { label: 'Fees Awarded', value: (graveyardStats?.total_fees || 0).toLocaleString(), icon: Trophy, color: '#fbbf24' },
            { label: 'Grave Keepers', value: graveyardStats?.unique_users || 0, icon: Users, color: '#818cf8' },
          ].map(s => (
            <Panel key={s.label} className="p-4">
              <div className="flex items-center gap-2 mb-2 relative z-10">
                <s.icon className="w-3.5 h-3.5" style={{ color: s.color, filter: `drop-shadow(0 0 4px ${s.color}60)` }} />
                <span className="text-[9px] tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(167,139,250,0.5)' }}>{s.label}</span>
              </div>
              <p className="text-2xl font-bold relative z-10" style={{
                fontFamily: "'Impact', 'Arial Black', sans-serif", color: s.color,
                filter: `drop-shadow(0 0 6px ${s.color}40)`,
              }}>{loading ? '...' : s.value}</p>
            </Panel>
          ))}
        </div>

        {/* ═══ SUBMIT FORM + YOUR STATS — responsive ═══ */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5 mb-6 md:mb-8">

          {/* Submit form — full on mobile */}
          <div className="md:col-span-8">
            <Panel glow className="p-6" data-testid="graveyard-submit-form">
              <div className="flex items-center gap-2 mb-4 relative z-10">
                <div className="p-1.5" style={{ background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.25)' }}>
                  <Skull className="w-4 h-4" style={{ color: '#a78bfa', filter: 'drop-shadow(0 0 4px rgba(139,92,246,0.5))' }} />
                </div>
                <span className="text-xs tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: '#a78bfa' }}>Bury Your Failed TX</span>
              </div>

              {(connected || isDemo) ? (
                <div className="relative z-10">
                  {userStats && (
                    <div className="flex items-center justify-between text-xs mb-4 px-4 py-2.5"
                      style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(139,92,246,0.1)' }}>
                      <span style={{ fontFamily: "'Courier New', monospace", color: 'rgba(167,139,250,0.5)' }}>
                        Today: <span className="font-bold" style={{ color: '#a78bfa' }}>{userStats.today_submissions || 0}</span> / {DAILY_LIMIT}
                      </span>
                      <span className="font-bold" style={{ fontFamily: "'Impact', sans-serif", color: '#a78bfa' }}>
                        +{(userStats.total_fees || 0).toLocaleString()} fees
                      </span>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <input type="text" value={txHash} onChange={e => setTxHash(e.target.value)}
                      placeholder="Paste failed TX signature..."
                      disabled={isSubmitting}
                      className="flex-1 px-4 py-3 text-sm outline-none"
                      style={{ fontFamily: "'Courier New', monospace", background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(139,92,246,0.15)', color: '#c4b5fd' }}
                      data-testid="graveyard-tx-input" />
                    <button type="submit"
                      disabled={isSubmitting || !txHash.trim() || (userStats?.today_submissions >= DAILY_LIMIT)}
                      className="px-8 py-3 text-sm font-bold tracking-wider uppercase flex items-center gap-2 transition-all"
                      style={{
                        fontFamily: "'Cinzel', serif",
                        background: isSubmitting ? 'rgba(139,92,246,0.08)' : 'rgba(139,92,246,0.15)',
                        border: '1px solid rgba(139,92,246,0.4)', color: '#a78bfa',
                        opacity: !txHash.trim() || (userStats?.today_submissions >= DAILY_LIMIT) ? 0.3 : 1,
                        boxShadow: '0 0 12px rgba(139,92,246,0.08)',
                      }}
                      data-testid="graveyard-submit-btn">
                      {isSubmitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying...</> :
                        userStats?.today_submissions >= DAILY_LIMIT ? <><Clock className="w-4 h-4" /> Limit Reached</> :
                        <><Send className="w-4 h-4" /> Submit</>}
                    </button>
                  </form>

                  <AnimatePresence>
                    {submitResult && (
                      <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="mt-3 flex items-start gap-2 px-4 py-3 text-xs font-bold"
                        style={{
                          background: submitResult.success ? 'rgba(139,92,246,0.08)' : 'rgba(239,68,68,0.06)',
                          border: `1px solid ${submitResult.success ? 'rgba(139,92,246,0.3)' : 'rgba(239,68,68,0.2)'}`,
                          fontFamily: "'Courier New', monospace",
                        }} data-testid="graveyard-submit-result">
                        {submitResult.success ? <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#a78bfa' }} /> : <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#ef4444' }} />}
                        <div>
                          <p style={{ color: submitResult.success ? '#c4b5fd' : '#fca5a5' }}>{submitResult.message}</p>
                          {submitResult.success && submitResult.fees_earned && (
                            <p className="mt-1" style={{ color: '#a78bfa' }}>
                              +{submitResult.fees_earned.toLocaleString()} fees
                              {submitResult.season_points && <span style={{ color: '#fbbf24' }}> +{submitResult.season_points} SP</span>}
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="mt-3 flex items-start gap-2">
                    <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: 'rgba(167,139,250,0.25)' }} />
                    <p className="text-[10px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(167,139,250,0.3)' }}>Only verified failed Solana transactions accepted. Duplicates rejected.</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 relative z-10">
                  <Shield className="w-10 h-10 mx-auto mb-3" style={{ color: 'rgba(139,92,246,0.3)', filter: 'drop-shadow(0 0 10px rgba(139,92,246,0.15))' }} />
                  <p className="text-base font-bold mb-1" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(167,139,250,0.7)' }}>Connect Wallet</p>
                  <p className="text-xs" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(167,139,250,0.4)' }}>Connect your Solana wallet to start burying failed transactions</p>
                </div>
              )}
            </Panel>
          </div>

          {/* Right sidebar — How It Works + Discord */}
          <div className="md:col-span-4 flex flex-col gap-2 md:gap-3">
            <Panel className="p-4">
              <span className="text-[10px] tracking-[0.15em] uppercase font-bold block mb-3 relative z-10" style={{ fontFamily: "'Cinzel', serif", color: '#a78bfa' }}>How It Works</span>
              {[
                { title: 'Find', desc: 'Locate a failed Solana TX from your wallet history', icon: Hash, color: '#818cf8' },
                { title: 'Submit', desc: 'Paste the TX signature above for on-chain verification', icon: Send, color: '#a78bfa' },
                { title: 'Earn', desc: `Get +${FEES_PER_FAILED_TX.toLocaleString()} fees and +${SP_PER_TX} SP per kill`, icon: Trophy, color: '#fbbf24' },
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-2.5 mb-3 last:mb-0 relative z-10">
                  <div className="p-1 flex-shrink-0 mt-0.5" style={{ background: `${s.color}10`, border: `1px solid ${s.color}25` }}>
                    <s.icon className="w-3 h-3" style={{ color: s.color }} />
                  </div>
                  <div>
                    <p className="text-xs font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(200,185,240,0.8)' }}>{s.title}</p>
                    <p className="text-[10px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(167,139,250,0.45)' }}>{s.desc}</p>
                  </div>
                </div>
              ))}
            </Panel>

            <Panel className="p-4">
              <span className="text-[10px] tracking-[0.15em] uppercase font-bold block mb-2 relative z-10" style={{ fontFamily: "'Cinzel', serif", color: '#a78bfa' }}>Rewards</span>
              <div className="grid grid-cols-2 gap-2 relative z-10">
                <div className="text-center py-2" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(139,92,246,0.08)' }}>
                  <p className="text-lg font-bold" style={{ fontFamily: "'Impact', sans-serif", color: '#a78bfa' }}>50K</p>
                  <p className="text-[8px] uppercase tracking-wider font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(167,139,250,0.4)' }}>Fees</p>
                </div>
                <div className="text-center py-2" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(139,92,246,0.08)' }}>
                  <p className="text-lg font-bold" style={{ fontFamily: "'Impact', sans-serif", color: '#fbbf24' }}>25</p>
                  <p className="text-[8px] uppercase tracking-wider font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(167,139,250,0.4)' }}>SP</p>
                </div>
              </div>
              <p className="text-[9px] mt-2 relative z-10" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(167,139,250,0.3)' }}>Up to {DAILY_LIMIT} submissions/day</p>
            </Panel>

            <Panel className="p-4">
              <a href="https://discord.gg/3wH7NaVXxP" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 relative z-10 group" data-testid="discord-cta">
                <Users className="w-4 h-4 group-hover:scale-110 transition-transform" style={{ color: '#818cf8' }} />
                <span className="text-xs font-bold tracking-wider" style={{ fontFamily: "'Cinzel', serif", color: '#818cf8' }}>The Sewer Syndicate</span>
                <ExternalLink className="w-3 h-3 ml-auto" style={{ color: 'rgba(129,140,248,0.35)' }} />
              </a>
            </Panel>
          </div>
        </div>

        {/* ═══ RECENT BURIALS ═══ */}
        <div className="mb-8" data-testid="graveyard-recent-burials">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-1.5" style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)' }}>
              <Skull className="w-4 h-4" style={{ color: '#a78bfa', filter: 'drop-shadow(0 0 4px rgba(139,92,246,0.4))' }} />
            </div>
            <span className="text-xs tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: '#a78bfa' }}>Recent Burials</span>
          </div>

          {recentSubmissions.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {recentSubmissions.slice(0, 9).map((tx, i) => (
                <Tombstone key={tx.tx_hash} tx={tx} index={i} />
              ))}
            </div>
          ) : (
            <Panel className="p-10 text-center">
              <Skull className="w-8 h-8 mx-auto mb-3 relative z-10" style={{ color: 'rgba(139,92,246,0.2)' }} />
              <p className="text-sm font-bold relative z-10" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(167,139,250,0.45)' }}>No failed transactions buried yet.</p>
              <p className="text-xs mt-1 relative z-10" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(167,139,250,0.3)' }}>Be the first to claim fees.</p>
            </Panel>
          )}
        </div>

        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-[9px] tracking-[0.2em] uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(167,139,250,0.25)' }}>
            Verified via Solana RPC. Only on-chain failed transactions qualify.
          </p>
        </div>
      </div>
    </div>
  );
}
